import { MonitorPriority } from "@p0security/shared/types/assessment/monitor";
import { useCallback, useContext, useMemo, useState } from "react";

import { SelectedEnvironmentContext } from "../../Environment/contexts/SelectedEnvironmentContext.tsx";
import { useDashboardFindings } from "../hooks/useDashboardFindings";
import { mockMonitorsByPriority } from "../zeroStateData/findings-zero-state";
import { FindingGraph } from "./FindingGraph";
import { OpenFindings } from "./OpenFindings";
import { PanelErrorBoundary } from "./common/PanelErrorBoundary";

const INITIAL_SELECTED_PRIORITIES = new Set<MonitorPriority>([
  "CRITICAL",
  "HIGH",
]);

export const FindingsBySeverity: React.FC<{
  isLoading: boolean;
  hasEnvironments: boolean;
  selectedMonitorIds: string[];
}> = ({ isLoading, hasEnvironments, selectedMonitorIds }) => {
  const [priorities, setPriorities] = useState(INITIAL_SELECTED_PRIORITIES);
  const { last } = useContext(SelectedEnvironmentContext);
  const {
    loading: findingsLoading,
    monitorsByPriority,
    startDate,
  } = useDashboardFindings(selectedMonitorIds);

  // this is a somewhat brittle hack until we have a better way to retrieve
  // the start date of the assessment runs
  // when no filter is present, set the start date to the start date of the findings
  const findingsStartDate = useMemo(
    () => (selectedMonitorIds.length === 0 ? startDate : 0),
    [selectedMonitorIds.length, startDate]
  );

  const handleSeverityChange = useCallback((v: MonitorPriority) => {
    setPriorities(
      (prev) =>
        new Set(
          prev.has(v)
            ? [...prev.keys()].filter((k) => k !== v)
            : [...prev.keys(), v]
        )
    );
  }, []);

  // Note that while a new assessment is run for the first time, panels will display zero counts.
  // Fixing this to display an indicator is a TODO.
  const loading = useMemo(
    () => isLoading || (hasEnvironments && !last.doc) || findingsLoading,
    [isLoading, hasEnvironments, last.doc, findingsLoading]
  );

  return (
    <div className="flex w-full">
      <PanelErrorBoundary title="Open Finding">
        <OpenFindings
          monitorsByPriority={
            hasEnvironments ? monitorsByPriority : mockMonitorsByPriority
          }
          isLoading={loading}
          handleChange={handleSeverityChange}
          priorities={priorities}
        />
      </PanelErrorBoundary>
      <PanelErrorBoundary title="Finding Graph">
        <FindingGraph
          isLoading={loading}
          monitors={
            hasEnvironments ? monitorsByPriority : mockMonitorsByPriority
          }
          priorities={priorities}
          startDate={findingsStartDate}
        />
      </PanelErrorBoundary>
    </div>
  );
};
